import type { PortalType, ProductType } from '@linksoft/tenant-registry';
import type { ComingSoonConfig } from './coming-soon';

/**
 * Everything about a portal that changes when the APP changes rather than when
 * the customer changes. Presets declare it, a deployment's registry `settings`
 * overrides it, and `resolvePortalSettings()` composes the two.
 *
 * `features` is intentionally loose: the catalogue in
 * [`app/assets/json/features.json`](../../app/assets/json/features.json) is the
 * real contract, and a preset or tenant naming an id that is not in it fails the
 * build.
 */
export interface PortalSettings {
	/** Page id `/` redirects to; also sorts first in the sidebar. Null clears it. */
	homePage?: string;
	/** Enabled page ids. Replaces (never appends to) a lower layer's list. */
	pages: string[];
	/**
	 * Feature id -> enablement plus flat config. `true`/`false` is shorthand for
	 * `{ enabled }`; an object may carry `enabled`, `components` and any config
	 * key the catalogue declares for that feature.
	 */
	features: Record<string, boolean | PortalFeatureSettings>;
	/**
	 * When true the app may fetch `/api-override.json` at runtime and let its
	 * `apiOrigin` replace the baked value, so a deployed static build can be
	 * repointed without a rebuild.
	 */
	allowApiOriginOverride?: boolean;
	/**
	 * Present on a "parked" portal: the whole site is public (no login) and
	 * renders only `/coming-soon`. `public/coming-soon.json` overrides any of
	 * these at runtime.
	 */
	comingSoon?: ComingSoonConfig;
}

export interface PortalFeatureSettings {
	enabled?: boolean;
	components?: Record<string, boolean>;
	[configKey: string]: unknown;
}

/** What a `presets/*.ts` file default-exports. */
export type PortalPreset = Partial<PortalSettings>;

/** Identity-typed so a preset file is checked against the settings shape. */
export function definePreset(preset: PortalPreset): PortalPreset {
	return preset;
}

/** The preset a deployment gets when it names none. */
export function defaultPresetName(productType: ProductType, portalType: PortalType): string {
	return `${productType}-${portalType}`;
}
